<template>
  <div class="step-content p-0 pt-2">
    <h3 class="step-title">Tipo do evento</h3>
    <p class="step-description">Selecione a ocasião e escolha os presentes que vão compor sua lista.</p>

    <div class="mb-4">
      <label class="form-label">Qual é a ocasião?</label>

      <!-- Loading -->
      <div v-if="loadingItems && eventTypes.length === 0" class="text-center text-muted py-3">
        <i class="fas fa-spinner fa-spin me-2"></i> Carregando ocasiões...
      </div>

      <!-- Cards de tipo de evento -->
      <div v-else class="event-type-chips">
        <button
          v-for="type in eventTypes"
          :key="type.id"
          type="button"
          :class="['event-type-chip', { 'event-type-chip--active': selectedEventType === type.id }]"
          @click="selectedEventType = type.id; emitEventType()"
        >
          <i
            v-if="selectedEventType === type.id"
            class="fas fa-check event-type-chip__check"
          ></i>
          <span class="event-type-chip__name">{{ type.name }}</span>
        </button>
      </div>

      <div v-if="errors.eventType" class="text-danger small mt-2">
        <i class="fas fa-exclamation-circle me-1"></i>{{ errors.eventType }}
      </div>
    </div>

    <div v-if="loadingItems" class="text-center text-muted py-3">
      <i class="fas fa-spinner fa-spin me-2"></i> Buscando presentes sugeridos...
    </div>

    <div v-else-if="eventItems.length > 0">
       <div class="items-toolbar mb-3">
        <button
        type="button"
        class="btn btn-sm btn-outline-secondary items-toolbar__toggle-btn"
        @click="toggleAllItems"
        >
          <i :class="[areAllItemsSelected ? 'fas fa-minus-circle' : 'fas fa-check-circle', 'me-2', 'items-toolbar__toggle-icon', { 'is-active': areAllItemsSelected }]" ></i>
          {{ areAllItemsSelected ? 'Desmarcar todos' : 'Selecionar todos' }}
        </button>
        <small class="small text-muted-dark items-toolbar__hint">Presentes sugeridos para a ocasião:</small>
      </div>

      <div class="event-items-grid">
        <div
          class="event-item-card"
          v-for="item in eventItems"
          :key="item.id"
          :class="{ 'event-item-card--selected': item.isSelected }"
          @click="toggleItemSelection(item.id)"
        >
          <div class="event-item-card__img">
            <img :src="item.imageUrl" :alt="item.name" />
          </div>
          <div class="event-item-card__body">
            <p class="event-item-card__name">{{ item.name }}</p>
            <p class="event-item-card__price">R$ {{ formatCurrency(item.price) }}</p>
          </div>
          <div class="event-item-card__check">
            <i v-if="item.isSelected" class="fas fa-check-circle"></i>
            <i v-else class="far fa-circle"></i>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="selectedEventType && !loadingItems && eventItems.length === 0" class="alert alert-info text-center mt-3">
        Nenhum presente sugerido para esta ocasião. Adicione um personalizado abaixo!
    </div>

    <div v-if="canShowCustomItemButton" class="mt-4 text-center">
        <button
            type="button"
            class="btn btn-primary-outline w-100 d-md-inline-flex justify-content-md-center align-items-md-center"
            @click="showCustomItemModal"
        :disabled="loadingItems"
        >
            <i class="fas fa-plus me-2"></i> Adicionar presente personalizado
        </button>
    </div>

    <!-- Modal: crop de imagem do item customizado -->
    <ImageCropperModal
      :visible="customItemCropperVisible"
      :src="customItemCropSource"
      :aspect-ratio="1"
      title="Ajuste a foto do presente"
      subtitle="Posicione e enquadre como preferir."
      @confirm="onCustomItemCropConfirm"
      @cancel="cancelCustomItemCrop"
    />

    <!-- Modal: adicionar item customizado -->
    <BottomModal
      :visible="customItemModalVisible"
      @close="cancelCustomItem"
      :fullHeight="false"
      :showCloseButton="true"
    >
      <template #default>
        <div class="custom-item-modal-content p-3">
          <h5 class="text-center mb-1 text-primary-emphasis">Novo presente personalizado</h5>
          <p class="text-center text-muted-dark mb-4">Adicione um presente que não está na lista sugerida.</p>

          <!-- Upload de imagem -->
          <div class="d-flex justify-content-center mb-4">
            <PhotoUploadButton
              :preview="newCustomItem.imagePreview"
              empty-label="Foto"
              label="Adicionar foto do presente"
              @click="triggerCustomItemImageInput"
              @remove="removeCustomItemImage"
            />
          </div>
          <input
            ref="customItemFileInput"
            type="file"
            accept="image/jpeg,image/png"
            class="d-none"
            @change="handleCustomItemImageUpload"
          />

          <div class="form-group mb-3">
            <label for="customItemName" class="form-label fw-semibold">Nome do presente</label>
            <input
              type="text"
              id="customItemName"
              class="form-control"
              v-model="newCustomItem.name"
              :placeholder="customItemPlaceholder"
              :class="{ 'is-invalid': errors.newCustomItemName }"
            >
            <div v-if="errors.newCustomItemName" class="invalid-feedback d-block">{{ errors.newCustomItemName }}</div>
          </div>

          <div class="form-group mb-3">
            <label for="customItemPrice" class="form-label fw-semibold">Valor (R$)</label>
            <CurrencyInput
              :options="currencyInputOptions"
              id="customItemPrice"
              class="form-control"
              v-model="newCustomItem.price"
              placeholder="0,00"
              :class="{ 'is-invalid': errors.newCustomItemPrice }"
            />
            <div v-if="errors.newCustomItemPrice" class="invalid-feedback d-block">{{ errors.newCustomItemPrice }}</div>
          </div>

          <div class="modal-buttons-row mt-4">
            <button type="button" class="btn btn-outline-secondary flex-grow-1" @click="cancelCustomItem">
              <i class="fas fa-times me-2"></i> Cancelar
            </button>
            <button type="button" class="btn btn-color-site flex-grow-1" @click="addCustomItem">
              <i class="fas fa-plus me-2"></i> Adicionar presente
            </button>
          </div>
        </div>
      </template>
    </BottomModal>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import { ref, onMounted, watch, defineExpose, computed, onUnmounted } from 'vue';
import BottomModal from '@/components/BottomModal.vue';
import CurrencyInput from '@/components/CurrencyInput.vue';
import ImageCropperModal from '@/components/ImageCropperModal.vue';
import PhotoUploadButton from '@/components/PhotoUploadButton.vue';
import giftCustomImage from '@/assets/images/gift-custom.png';
import { formatCurrency } from '@/utils/currencyFormatter';
import api from '@/services/api';
import { getFees } from '@/services/feesService';
import { setWithExpiry, getWithExpiry } from '@/utils/localStorageCache';

import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'Step1EventType',
  emits: ['update:eventType', 'update:selectedItems', 'update:eventTypeName'],
  components: {
    BottomModal,
    CurrencyInput,
    ImageCropperModal,
    PhotoUploadButton
  },
  setup(props, { emit }) {
    const eventTypes = ref([]);
    const selectedEventType = ref('');
    const eventItems = ref([]); 
    const selectedItems = ref({}); 
    const loadingItems = ref(false);
    const errors = ref({});
    const valueLimits = ref({
      min_event_item_price: 10,
      max_event_item_price: 5000,
      max_cart_amount: 20000,
    });
    
    const currentPlaceholderIndex = ref(0);
    const customItemPlaceholder = ref('Ex: Bolo de 3 andares'); 
    let placeholderInterval = null;

    const CACHE_KEY_EVENT_TYPES = 'cachedEventTypes';
    const CACHE_TTL_HOURS = 8; 

    const currencyInputOptions = computed(() => ({
        locale: 'pt-BR', 
        currency: 'BRL',
        currencyDisplay: 'symbol',
        precision: 2,
        hideCurrencySymbolOnFocus: false,
        hideGroupingSeparatorOnFocus: false,
        autoDecimalDigits: true,
    }));

    const customItemModalVisible = ref(false);
    const newCustomItem = ref({ name: '', price: null, imagePreview: null, imageBlob: null });

    // Crop do item customizado
    const customItemFileInput = ref(null);
    const customItemCropperVisible = ref(false);
    const customItemCropSource = ref('');

    const formatLimitCurrency = (value) => `R$ ${formatCurrency(Number(value) || 0)}`;

    const fetchValueLimits = async () => {
      try {
        const config = await getFees();
        const limits = config?.limits || {};
        valueLimits.value = {
          min_event_item_price: Number(limits.min_event_item_price ?? limits.event_item?.min_price ?? valueLimits.value.min_event_item_price),
          max_event_item_price: Number(limits.max_event_item_price ?? limits.event_item?.max_price ?? valueLimits.value.max_event_item_price),
          max_cart_amount: Number(limits.max_cart_amount ?? limits.cart?.max_amount ?? valueLimits.value.max_cart_amount),
        };
      } catch (error) {
        console.warn('Não foi possível carregar limites de valores.', error);
      }
    };

    const validateItemPriceLimit = (price) => {
      const value = Number(price);
      const min = Number(valueLimits.value.min_event_item_price) || 10;
      const max = Number(valueLimits.value.max_event_item_price) || 5000;
      if (!value || value <= 0) return 'O valor deve ser um número positivo.';
      if (value < min || value > max) {
        return `O valor deve ficar entre ${formatLimitCurrency(min)} e ${formatLimitCurrency(max)}.`;
      }
      return '';
    };

    const validateSelectedItemsLimits = () => {
      const items = Object.values(selectedItems.value || {});
      for (const item of items) {
        const priceError = validateItemPriceLimit(item?.price);
        if (priceError) return priceError;
      }
      const total = items.reduce((sum, item) => sum + ((Number(item?.price) || 0) * (Number(item?.quantity) || 1)), 0);
      const maxCart = Number(valueLimits.value.max_cart_amount) || 20000;
      if (total > maxCart) return `O valor máximo por carrinho é de ${formatLimitCurrency(maxCart)}.`;
      return '';
    };

    function triggerCustomItemImageInput() {
      customItemFileInput.value?.click();
    }

    function handleCustomItemImageUpload(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        toast.error('Use uma imagem JPG ou PNG.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A imagem deve ter no máximo 5 MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        customItemCropSource.value = e.target.result;
        customItemCropperVisible.value = true;
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    }

    function cancelCustomItemCrop() {
      customItemCropperVisible.value = false;
      customItemCropSource.value = '';
    }

    function removeCustomItemImage() {
      newCustomItem.value.imagePreview = null;
      newCustomItem.value.imageBlob = null;
      if (customItemFileInput.value) customItemFileInput.value.value = '';
    }

    function onCustomItemCropConfirm({ blob, dataUrl }) {
      newCustomItem.value.imagePreview = dataUrl;
      newCustomItem.value.imageBlob = blob;
      customItemCropperVisible.value = false;
      customItemCropSource.value = '';
    }

    const areAllItemsSelected = computed(() => {
      return eventItems.value.length > 0 && eventItems.value.every(item => item.isSelected);
    });

    const canShowCustomItemButton = computed(() => {
      return selectedEventType.value !== null
        && selectedEventType.value !== undefined
        && String(selectedEventType.value).trim() !== '';
    });

    const fetchEventTypes = async () => {
      loadingItems.value = true;
      try {
        const cachedData = getWithExpiry(CACHE_KEY_EVENT_TYPES);
        if (cachedData) {
          eventTypes.value = cachedData;
          console.log('Tipos de evento carregados do cache!');
          if (selectedEventType.value) {
            updateCustomItemPlaceholder();
          }
          return;
        }

        const response = await api.get('/events/type');
        eventTypes.value = response.data;
        setWithExpiry(CACHE_KEY_EVENT_TYPES, response.data, CACHE_TTL_HOURS);
        console.log('Tipos de evento carregados da API e cacheados.');

      } catch (error) {
        console.error('Erro ao buscar tipos de evento:', error);
        eventTypes.value = [];
      } finally {
        loadingItems.value = false;
        if (selectedEventType.value) {
            updateCustomItemPlaceholder();
        }
      }
    };

    const fetchEventItems = async (eventTypeToFetch) => {
      if (!eventTypeToFetch) {
        eventItems.value = [];
        selectedItems.value = {};
        emitSelectedItems();
        stopPlaceholderRotation(); 
        customItemPlaceholder.value = 'Ex: Bolo de 3 andares'; 
        return;
      }
      loadingItems.value = true;
      try {
        const response = await api.get(`/events/type/${eventTypeToFetch}`);
        eventItems.value = response.data.map(item => ({
            id: item.id,
            name: item.description,
            price: item.price,
            imageUrl: item.photo_url,
            category: item.category,
            isSelected: false, 
            quantity: 1
        }));
      } catch (error) {
        console.error(`Erro ao buscar itens para o tipo ${eventTypeToFetch}:`, error);        
        eventItems.value = []; 
      } finally {
        selectedItems.value = {}; 
        emitSelectedItems();
        loadingItems.value = false;
        updateCustomItemPlaceholder();
      }
    };

    const updateCustomItemPlaceholder = () => {
      stopPlaceholderRotation();

      const selectedType = eventTypes.value.find(type => type.id === selectedEventType.value);
      if (selectedType && selectedType.placeholders && selectedType.placeholders.length > 0) {
        currentPlaceholderIndex.value = 0;
        customItemPlaceholder.value = selectedType.placeholders[currentPlaceholderIndex.value];

        if (selectedType.placeholders.length > 1) {
          placeholderInterval = setInterval(() => {
            currentPlaceholderIndex.value = (currentPlaceholderIndex.value + 1) % selectedType.placeholders.length;
            customItemPlaceholder.value = selectedType.placeholders[currentPlaceholderIndex.value];
          }, 4000); 
        }
      } else {
        customItemPlaceholder.value = 'Ex: Item personalizado'; 
      }
    };

    const stopPlaceholderRotation = () => {
      if (placeholderInterval) {
        clearInterval(placeholderInterval);
        placeholderInterval = null;
      }
    };

    const validateEventType = () => {
      errors.value = {};
      if (!selectedEventType.value) {
        errors.value.eventType = 'Por favor, selecione um tipo de evento.';
        return false;
      }
      return true;
    };

    const validateAndProceed = (showToast = false) => {
      const isEventTypeValid = validateEventType();
      const hasSelectedItems = Object.keys(selectedItems.value).length > 0;

      if (isEventTypeValid && hasSelectedItems) {
        const limitsError = validateSelectedItemsLimits();
        if (limitsError) {
          if (showToast) {
            toast.error(limitsError, {
              autoClose: 3000,
              position: 'top-right',
            });
          }
          return { isValid: false, data: null };
        }
        return {
          isValid: true,
          data: {
            eventType: selectedEventType.value,
            selectedItems: selectedItems.value
          }
        };
      } else {
        if (showToast) {
          if (!isEventTypeValid) {
            toast.error('Por favor, selecione um tipo de evento para continuar.', {
              autoClose: 3000,
              position: 'top-right',
            });
          } else if (!hasSelectedItems) {
            toast.error('Por favor, selecione ao menos um item (ou adicione um customizado) para o seu evento.', {
              autoClose: 3000,
              position: 'top-right',
            });
          }
        }
        return { isValid: false, data: null };
      }
    };

    const toggleItemSelection = (itemId) => {
      const item = eventItems.value.find(i => i.id === itemId);
      if (item) {
        item.isSelected = !item.isSelected;
        emitSelectedItems();
      }
    };

    const toggleAllItems = () => {
      const targetSelectionState = !areAllItemsSelected.value; 
      eventItems.value.forEach(item => {
        item.isSelected = targetSelectionState;
      });
      emitSelectedItems();
    };

    const emitSelectedItems = () => {
        const itemsToEmit = eventItems.value
            .filter(item => item.isSelected)
            .map(item => ({
                id: item.id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                imageUrl: item.imageUrl,
                category: item.category
            }));

        const selectedItemsObject = itemsToEmit.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {});

        selectedItems.value = selectedItemsObject;
        emit('update:selectedItems', selectedItems.value);
    };

    const showCustomItemModal = () => {
      customItemModalVisible.value = true;
      newCustomItem.value = { name: '', price: null, imagePreview: null, imageBlob: null };
      errors.value = {};
      updateCustomItemPlaceholder(); 
    };

    const cancelCustomItem = () => {
      customItemModalVisible.value = false;
      errors.value = {};
      stopPlaceholderRotation(); 
    };

    const addCustomItem = () => {
      errors.value = {};
      let isValid = true;

      if (!newCustomItem.value.name.trim()) {
        errors.value.newCustomItemName = 'O nome do item é obrigatório.';
        isValid = false;
      }

      const priceError = validateItemPriceLimit(newCustomItem.value.price);
      if (priceError) {
        errors.value.newCustomItemPrice = priceError;
        isValid = false;
      }

      if (isValid) {
        const customId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : uuidv4(); 

        const newItem = {
          id: customId,
          name: newCustomItem.value.name.trim(),
          description: 'Item adicionado pelo usuário.',
          price: newCustomItem.value.price,
          imageUrl: newCustomItem.value.imagePreview || '',
          category: 'Personalizado',
          isSelected: true,
          quantity: 1
        };

        eventItems.value.push(newItem);
        customItemModalVisible.value = false;
        emitSelectedItems();
        toast.success('Item customizado adicionado com sucesso!', {
          autoClose: 3000,
          position: 'top-right',
        });
        stopPlaceholderRotation(); 
      }
    };
    const emitEventType = () => {
      emit('update:eventType', { eventType: selectedEventType.value });
      validateEventType();
      const typeFound = eventTypes.value.find(t => t.id === selectedEventType.value);
      emit('update:eventTypeName', typeFound ? typeFound.name : '');
    };

    watch(selectedEventType, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        fetchEventItems(newValue);
      }
    });

    onMounted(() => {
      fetchValueLimits();
      fetchEventTypes();
    });

    onUnmounted(() => { 
      stopPlaceholderRotation();
    });

    const resetSelection = () => {
      selectedEventType.value = '';
      eventItems.value = [];
      selectedItems.value = {};
      errors.value = {};
    };

    defineExpose({
      validateAndProceed,
      eventTypes,
      resetSelection,
    });

    return {
      eventTypes,
      selectedEventType,
      eventItems,
      loadingItems,
      errors,
      customItemModalVisible,
      newCustomItem,
      customItemPlaceholder,
      currencyInputOptions,
      areAllItemsSelected, 
      canShowCustomItemButton,
      // crop do item customizado
      customItemFileInput,
      customItemCropperVisible,
      customItemCropSource,
      triggerCustomItemImageInput,
      handleCustomItemImageUpload,
      cancelCustomItemCrop,
      removeCustomItemImage,
      onCustomItemCropConfirm,
      // ações
      toggleItemSelection,
      toggleAllItems,
      emitEventType,
      validateAndProceed,
      showCustomItemModal,
      cancelCustomItem,
      addCustomItem,
      formatCurrency
    };
  }
};
</script>

<style scoped>
.step-content { padding: 1rem 0; }
.step-title { font-size: 1.25rem; font-weight: 700; color: #32355d; margin-bottom: 0.25rem; }
.step-description { font-size: 0.95rem; color: #6c757d; margin-bottom: 1.5rem; line-height: 1.5; }
.form-select {
  height: calc(2.25rem + 2px); padding: 0.375rem 2.25rem 0.375rem 0.75rem; line-height: 1.5; color: #495057; background-color: #fff; background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e"); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 16px 12px; border: 1px solid #ced4da; border-radius: 0.5rem; appearance: none;
}
.form-select:focus { border-color: var(--primary-color, #ff50a3); box-shadow: 0 0 0 0.25rem rgba(255, 80, 163, 0.25); outline: none; }
h4 { 
    font-size: 1.4rem; 
    font-weight: 600; 
    color: #32355d; 
    margin-bottom: 0; 
}

/* Novo estilo para o texto "Itens Sugeridos:" */
.text-muted-dark {
    font-size: 0.85em; /* Torna o texto um pouco menor que o H4 normal */
    color: #555 !important; /* Cinza escuro */
    font-weight: normal; /* Garante que não herde o bold do h4 */
}


.event-items-list { display: flex; flex-direction: column; gap: 1rem; width: 100%; }
.event-item-compact-card {
  display: flex; align-items: center; background-color: #ffffff; border: 1px solid #e0e0e0; border-radius: 0.75rem; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05); padding: 0.75rem 1rem; transition: box-shadow 0.2s ease-in-out, border-color 0.2s ease, background-color 0.2s ease; cursor: pointer; min-width: 0; width: 100%; overflow: hidden;
}
.event-item-compact-card.is-selected { border-color: #ff50a3; box-shadow: 0 4px 12px rgba(255, 80, 163, 0.2); background-color: #fff0f6; }
.event-item-compact-card:hover { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); }
.item-image {
  width: 60px; height: 60px; min-width: 60px; border-radius: 0.5rem; overflow: hidden; margin-right: 1rem; display: flex; justify-content: center; align-items: center; background-color: #f0f0f0;
}
.item-image img { width: 100%; height: 100%; object-fit: cover; }
.item-details { flex-grow: 1; min-width: 0; overflow: hidden; }
.item-name { font-size: 0.85rem; font-weight: 600; color: #32355d; margin: 0 0 0.15rem 0; line-height: 1.2; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.item-price { font-size: 1rem; color: #ff50a3; margin-bottom: 0; }
.item-selection-indicator { font-size: 1.5rem; color: #ced4da; margin-left: 1rem; }
.item-selection-indicator .fa-check-circle { color: #ff50a3; }
.btn-primary-outline {
  background-color: transparent; color: #ff50a3; border: 1px solid #ff50a3; padding: 0.75rem 1.25rem; border-radius: 0.5rem; font-weight: 600; transition: all 0.2s ease; display: inline-flex; align-items: center; justify-content: center;
}
.btn-primary-outline:hover { background-color: #ff50a3; color: white; }
.btn-color-site {
  background-color: #ff50a3; color: white; border: 1px solid #ff50a3; padding: 0.75rem 1.25rem; border-radius: 0.5rem; font-weight: 600; transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}
.btn-color-site:hover { background-color: #e6408f; border-color: #e6408f; color: white; }
.btn-color-site:active { background-color: #d1307b !important; border-color: #d1307b !important; color: white !important; }
.btn-color-site:focus { box-shadow: 0 0 0 0.25rem rgba(255, 80, 163, 0.25); }
.btn-outline-secondary {
  background-color: transparent; color: #6c757d; border: 1px solid #6c757d; padding: 0.75rem 1.25rem; border-radius: 0.5rem; font-weight: 600; transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}
.btn-outline-secondary.btn-sm {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.5;
    border-radius: 0.25rem;
}

.btn-outline-secondary:hover { background-color: #6c757d; color: white; border-color: #6c757d; }
.btn-outline-secondary:active { background-color: #5a6268 !important; border-color: #5a6268 !important; color: white !important; }
.btn-outline-secondary:focus { box-shadow: 0 0 0 0.25rem rgba(108, 117, 125, 0.25); }
.form-control.is-invalid { border-color: #dc3545; }
.invalid-feedback.d-block { display: block !important; color: #dc3545; font-size: 0.875em; margin-top: 0.25rem; }
.custom-item-modal-content { padding: 1rem; }
.modal-buttons-row { display: flex; gap: 0.75rem; justify-content: space-between; align-items: center; width: 100%; }

@media (max-width: 575.98px) {
  .event-item-compact-card { flex-wrap: nowrap; justify-content: flex-start; }
  .item-image { margin-right: 0.75rem; }
  .item-details { flex-basis: auto; max-width: none; }
  .item-name { font-size: 0.8rem; }
  .item-price { font-size: 0.95rem; }
  .item-selection-indicator { font-size: 1.3rem; margin-left: 0.75rem; }
  .modal-buttons-row .btn { padding: 0.6rem 0.8rem; font-size: 0.9rem; }
}

.small{    font-size: 0.75rem;    color: #6c757d;}

.items-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.items-toolbar__hint {
  margin: 0;
}

.items-toolbar__toggle-btn {
  white-space: nowrap;
  background: #fff;
  color: #32355d;
  border: 1.5px solid #e0e0e0;
  border-radius: 0.75rem;
  font-weight: 600;
}

.items-toolbar__toggle-btn:hover {
  background: #fff;
  color: #32355d;
  border-color: #d6d6d6;
}

.items-toolbar__toggle-btn:active {
  background: #fff !important;
  color: #32355d !important;
  border-color: #cfcfcf !important;
}

.items-toolbar__toggle-btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(50, 53, 93, 0.12);
}

.items-toolbar__toggle-icon {
  color: #9ca3af;
  transition: color 0.18s ease;
}

.items-toolbar__toggle-icon.is-active {
  color: #22c55e;
}

/* ── Chips de tipo de evento ─────────────────────────────── */
.event-type-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.event-type-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 16px;
  background: #fff;
  border: 1.5px solid #e0e0e0;
  border-radius: 99px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  line-height: 1;
  transition: all 0.18s ease;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.event-type-chip:hover {
  border-color: #ff50a3;
  color: #ff50a3;
  background: #fff0f6;
  box-shadow: 0 2px 8px rgba(255, 80, 163, 0.12);
}

.event-type-chip--active {
  background: #ff50a3;
  border-color: #ff50a3;
  color: #fff;
  box-shadow: 0 2px 10px rgba(255, 80, 163, 0.3);
}

.event-type-chip--active:hover {
  background: #e8417f;
  border-color: #e8417f;
  color: #fff;
}

.event-type-chip__check {
  font-size: 0.7rem;
  color: #fff;
}

/* ── Grid de itens do evento ───────────────────────────────── */
.event-items-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

@media (min-width: 480px) {
  .event-items-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 575.98px) {
  .items-toolbar {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .items-toolbar__toggle-btn {
    width: 100%;
  }

  .items-toolbar__hint {
    text-align: left;
  }
}

.event-item-card {
  background: #fff;
  border: 1.5px solid #e0e0e0;
  border-radius: 0.875rem;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: all 0.18s;
}

.event-item-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.event-item-card--selected {
  border-color: #ff50a3;
  background: #fff0f6;
  box-shadow: 0 4px 12px rgba(255, 80, 163, 0.18);
}

.event-item-card__img {
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #f8f8f8;
}

.event-item-card__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.event-item-card__body {
  padding: 0.5rem 0.6rem 0.6rem;
}

.event-item-card__name {
  font-size: 0.75rem;
  font-weight: 600;
  color: #32355d;
  margin: 0 0 2px;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.event-item-card__price {
  font-size: 0.85rem;
  font-weight: 700;
  color: #ff50a3;
  margin: 0;
}

.event-item-card__check {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 1rem;
  color: #ff50a3;
  filter: drop-shadow(0 1px 2px rgba(255,255,255,0.9));
}
</style>